import { FC, useState } from 'react';
import type { DayPlanGroupRow } from '@trafficpulse/shared';
import { slotChoiceKeys } from './slot-choice-keys';
import * as Popover from '@radix-ui/react-popover';
import { List, Search } from 'lucide-react';

import { useListGroupResolve } from '@/features/list-groups';

import type { DayPlanDropRow, DraftSlot } from '../../../../../drop-generator.types';
import type { SlotPatch } from '../../../state/slots/slot-ops';
import type { GroupRef } from '../../hooks/use-general-times';
import { ListCellLabel, listCellTitle } from './cells/ListCellLabel';
import { emptyListsWords, groupListsView } from './cells/planned-lists';
import { selectSlotList, selectSlotRank } from './slot-list-selection';
import { SlotListOptions } from './SlotListOptions';

const CELL =
  'group/list flex min-h-6 w-full min-w-0 items-center gap-1.5 rounded-md px-1.5 py-0.5 text-left transition-colors hover:bg-muted/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary data-[state=open]:bg-muted/70';

/** A drop's list for one of its groups (19/10/2026): the exact list(s) this drop goes to, one click to pin another. */
export const SlotListPin: FC<{
  group?: GroupRef;
  groups: GroupRef[];
  slot: DraftSlot;
  position: number;
  /** The row's planned drops (its own and its per-list ones). */
  planned?: DayPlanDropRow[];
  planGroup?: DayPlanGroupRow;
  onPatch: (patch: SlotPatch) => void;
}> = ({ group, groups, slot, position, planned = [], planGroup, onPatch }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const resolved = useListGroupResolve(group?.id ?? null);
  const lists = resolved.data?.lists ?? [];
  const pin = group ? slot.listsByGroup?.[group.id] : undefined;
  const pinName = pin === undefined ? null : (lists.find((list) => list.wiserId === pin)?.name ?? `List #${pin}`);
  const onlyName = lists.length === 1 ? (lists[0].name ?? `List #${lists[0].wiserId}`) : null;
  const rank = group ? slot.listsRankByGroup?.[group.id] : undefined;
  const view = group ? groupListsView({ group, planned, planGroup, pinName, onlyName, rank }) : null;
  const today = (planGroup?.listsToday ?? []).map((l) => l.name ?? `List #${l.wiserId}`);
  const listCount = resolved.data ? lists.length : null;
  return (
    <Popover.Root
      open={open}
      onOpenChange={(value) => {
        setOpen(value);
        setQuery('');
      }}
    >
      <Popover.Trigger asChild>
        <button
          type="button"
          aria-label={`Drop ${position}: ${group ? `list for ${group.name}` : 'choose a list'}`}
          title={view ? listCellTitle(view, emptyListsWords(view, listCount)) : 'Choose a list'}
          className={CELL}
        >
          {view ? (
            <ListCellLabel view={view} listCount={listCount} />
          ) : (
            <>
              <List className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              <span className="text-xs text-muted-foreground">Choose list</span>
            </>
          )}
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="start"
          sideOffset={6}
          collisionPadding={12}
          onKeyDown={slotChoiceKeys}
          className="z-[250] w-[min(480px,calc(100vw-24px))] rounded-xl border bg-popover p-2 text-popover-foreground shadow-xl"
        >
          <p className="px-2 pb-2 pt-1 text-xs font-semibold">Choose a list{group ? ` · ${group.name}` : ''}</p>
          <label className="mx-1 flex h-8 items-center gap-2 rounded-md border border-input px-2">
            <Search className="h-3.5 w-3.5 text-muted-foreground" />
            <input
              autoFocus
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Find a list…"
              aria-label={`Search lists for drop ${position}`}
              className="min-w-0 flex-1 bg-transparent text-xs outline-none"
            />
          </label>
          <div role="listbox" aria-label={`Lists for drop ${position}`} className="mt-1 max-h-[22rem] overflow-y-auto overscroll-contain">
            {groups.map((ref) => (
              <SlotListOptions
                key={ref.id}
                group={ref}
                active={slot.listGroupIds.includes(ref.id)}
                pin={slot.listsByGroup?.[ref.id]}
                rank={slot.listsRankByGroup?.[ref.id]}
                today={ref.id === group?.id ? today : []}
                query={query}
                onSelect={(id) => {
                  onPatch(selectSlotList(slot, ref.id, id));
                  setOpen(false);
                }}
                onRank={(k) => {
                  onPatch(selectSlotRank(slot, ref.id, k));
                  setOpen(false);
                }}
              />
            ))}
            {!groups.length ? <p className="p-2 text-xs text-muted-foreground">Add a list group in the List groups tab first.</p> : null}
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};
